<template>
  <!-- Mobile/Tablet Overlay -->
  <div
    v-if="showMobileOverlay"
    class="mobile-overlay"
    @click="closeMobileSidebar"
  ></div>

  <div
    class="content-sidebar"
    :class="{
      collapsed: collapsed,
      'mobile-slide-in': showMobileOverlay,
      'mobile-hidden': !showMobileOverlay,
    }"
  >
    <!-- Desktop toggle button (hidden on mobile) -->
    <div class="sidebar-header desktop-only">
      <button
        class="sidebar-toggle"
        @click="toggleSidebar"
        :title="collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
        :aria-label="collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
        :aria-expanded="!collapsed"
      >
        <ChevronLeftIcon
          class="toggle-icon"
          :style="{
            transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
          }"
        />
      </button>
    </div>

    <!-- Mobile close button -->
    <button
      class="mobile-close-btn mobile-only"
      @click="closeMobileSidebar"
      aria-label="Close Sidebar"
    >
      <img src="/images/close.svg" alt="Close" />
    </button>

    <div class="sidebar-content" v-if="!collapsed || showMobileOverlay">
      <!-- Edit actions (only show when in edit mode) -->
      <div v-if="isEdit" class="edit-actions">
        <button class="discard-button" @click="$emit('discard')">
          <v-icon>mdi-close-circle-outline</v-icon> Discard
        </button>
        <button class="save-button" @click="$emit('save')">
          <v-icon>mdi-content-save</v-icon> Save
        </button>
      </div>

      <div class="sidebar-title">
        <div class="content-info-heading">
          <span>Content Information</span>
        </div>
      </div>

      <div class="content-list">
        <div v-if="gradeName" class="content-item">
          <div class="content-title">Grade</div>
          <div class="content-value">
            <span class="value-tag">{{ gradeName }}</span>
          </div>
        </div>

        <div v-if="subjectName" class="content-item">
          <div class="content-title">Subject</div>
          <div class="content-value">
            <span class="value-tag">{{ subjectName }}</span>
          </div>
        </div>

        <div v-if="skillsetName" class="content-item">
          <div class="content-title">ESTREAM Skillset</div>
          <div class="content-value">
            <span class="value-tag">{{ skillsetName }}</span>
          </div>
        </div>

        <div v-if="mindsetName" class="content-item">
          <div class="content-title">ESTREAM Mindset</div>
          <div class="content-value">
            <span class="value-tag">{{ mindsetName }}</span>
          </div>
        </div>

        <div v-if="softwareName" class="content-item">
          <div class="content-title">Software</div>
          <div class="content-value">
            <span class="value-tag">{{
              Array.isArray(softwareName)
                ? softwareName.join(", ")
                : softwareName
            }}</span>
          </div>
        </div>

        <div
          v-if="parsedStudentProfiles && parsedStudentProfiles.length > 0"
          class="content-item"
        >
          <div class="content-title">Student Profile</div>
          <div class="content-value">
            <span
              v-for="(profile, index) in parsedStudentProfiles"
              :key="index"
              class="value-tag"
            >
              {{ profile }}
            </span>
          </div>
        </div>
        <div
          v-if="parsedAcademicStandards && parsedAcademicStandards.length > 0"
          class="content-item"
        >
          <div class="content-title">Academic Standards</div>
          <div class="content-value">
            <template
              v-for="(standard, index) in parsedAcademicStandards"
              :key="index"
            >
              <template v-if="standard.description">
                <v-tooltip
                  :text="standard.description"
                  location="top"
                  max-width="300"
                  content-class="custom-tooltip"
                >
                  <template v-slot:activator="{ props }">
                    <span class="value-tag" v-bind="props">
                      {{ standard.title }}
                    </span>
                  </template>
                </v-tooltip>
              </template>
              <template v-else>
                <span class="value-tag">{{ standard.title }}</span>
              </template>
            </template>
          </div>
        </div>

        <div v-if="futureReadySkillset" class="content-item">
          <div class="content-title">Future-ready Skill Set</div>
          <div class="content-value">
            <span class="value-tag">{{ futureReadySkillset }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  defineProps,
  defineEmits,
  ref,
  watch,
  onMounted,
  onUnmounted,
  computed,
} from "vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
  showMobile: {
    type: Boolean,
    default: false,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  gradeName: {
    type: String,
    default: "",
  },
  subjectName: {
    type: String,
    default: "",
  },
  skillsetName: {
    type: String,
    default: "",
  },
  mindsetName: {
    type: String,
    default: "",
  },
  softwareName: {
    type: [String, Array],
    default: "",
  },
  studentProfiles: {
    type: Array,
    default: () => [],
  },
  academicStandard: {
    type: [Array, Object, String],
    default: () => [],
  },
  futureReadySkillset: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "update:collapsed",
  "update:showMobile",
  "discard",
  "save",
]);

const showMobileOverlay = ref(false);

// Helper function to parse student profiles
const parseStudentProfiles = (profiles) => {
  if (!profiles) return [];

  // If it's already an array, return it
  if (Array.isArray(profiles)) {
    return profiles;
  }

  // If it's a string, try to parse it as JSON
  if (typeof profiles === "string") {
    try {
      const parsed = JSON.parse(profiles);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn("Failed to parse student profiles:", error);
      return [];
    }
  }

  return [];
};

// Helper function to parse academic standards
const parseAcademicStandards = (standards) => {
  if (!standards) return [];

  // If it's an array, process each item
  if (Array.isArray(standards)) {
    return standards.flatMap((item) => {
      // If item is an object, return objects with title and description
      if (typeof item === "object" && item !== null && !Array.isArray(item)) {
        return Object.entries(item).map(([key, value]) => ({
          title: key,
          description: value,
        }));
      }
      // If item is a string, return it as object with title only
      return [{ title: item, description: null }];
    });
  }

  // If it's a single object (not an array), return its entries as objects
  if (
    typeof standards === "object" &&
    standards !== null &&
    !Array.isArray(standards)
  ) {
    return Object.entries(standards).map(([key, value]) => ({
      title: key,
      description: value,
    }));
  }

  // If it's a string, try to parse it as JSON
  if (typeof standards === "string") {
    try {
      const parsed = JSON.parse(standards);
      return parseAcademicStandards(parsed); // Recursively process the parsed data
    } catch (error) {
      console.warn("Failed to parse academic standards:", error);
      return [];
    }
  }

  return [];
};

// Computed property for parsed student profiles
const parsedStudentProfiles = computed(() => {
  return parseStudentProfiles(props.studentProfiles);
});

// Computed property for parsed academic standards
const parsedAcademicStandards = computed(() => {
  return parseAcademicStandards(props.academicStandard);
});

const toggleSidebar = () => {
  emit("update:collapsed", !props.collapsed);
};

const closeMobileSidebar = () => {
  showMobileOverlay.value = false;
  emit("update:showMobile", false);
};

// Watch for mobile show prop changes
watch(
  () => props.showMobile,
  (newValue) => {
    showMobileOverlay.value = newValue;
    if (newValue) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }
  }
);

// Handle escape key to close mobile sidebar
const handleEscKey = (event) => {
  if (event.key === "Escape" && showMobileOverlay.value) {
    closeMobileSidebar();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleEscKey);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.body.classList.remove("sidebar-open");
});
</script>

<style scoped>
/* Desktop Styles */
.content-sidebar {
  width: 300px;
  min-width: 300px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  height: fit-content;
  position: sticky;
  top: 20px;
  transition: all 0.3s ease;
}

.content-sidebar.collapsed {
  width: 60px;
  min-width: 60px;
}

.sidebar-header {
  padding: 15px;
  display: flex;
  justify-content: flex-end;
  border-radius: 12px 12px 0 0;
}

.sidebar-content {
  padding: 0;
}

.edit-actions {
  display: flex;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid #e5e7eb;
  gap: 10px;
}

.discard-button {
  background-color: #e00069;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
}

.discard-button:hover {
  background-color: #c1005a;
}

.save-button {
  background-color: #00925e;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
}

.save-button:hover {
  background-color: #007a4f;
}

.content-info-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 15px 20px;
  color: var(--primary-blue);
  font-weight: 500;
  font-size: var(--fs-big);
}

.content-list {
  padding: 0;
}

.content-item {
  padding: 0;
}

.content-title {
  font-weight: 700;
  color: var(--black-body-text);
  padding: 15px 20px 8px;
  font-size: var(--fs-big);
}

.content-value {
  padding: 0 20px 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.value-tag {
  background-color: var(--white-hover-1);
  border: 1px solid var(--white-hover-1);
  border-radius: 6px;
  font-size: var(--text-small, 0.85rem);
  padding: 6px 12px;
  color: var(--black-body-text);
  display: inline-block;
  transition: all 0.2s ease;
  cursor: default;
}

.value-tag:hover {
  background-color: #e5e7eb;
  border-color: #d1d5db;
}

.sidebar-title {
  margin-bottom: 0;
}

.sidebar-toggle {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-toggle:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.toggle-icon {
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
}

/* Mobile overlay */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.mobile-close-btn {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 1000;
  background-color: transparent;
  border: none;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-close-btn img {
  width: 20px;
  height: 20px;
}

/* Responsive visibility classes */
.desktop-only {
  display: flex;
}

.mobile-only {
  display: none;
}

/* Global body class when sidebar is open on mobile */
:global(.sidebar-open) {
  overflow: hidden;
}

/* Mobile/Tablet Styles */
@media (max-width: 1024px) {
  .content-sidebar {
    position: fixed;
    top: auto;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: auto;
    max-height: 85vh;
    z-index: 999;
    padding-top: 0;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: 0px -4px 15px rgba(0, 0, 0, 0.15);
    border-radius: 28px 28px 0 0;
    border-top: 1px solid #d0d5dd;
    background-color: white;
  }

  /* Drag handle visual indicator */
  .content-sidebar::before {
    content: "";
    display: block;
    width: 40px;
    height: 4px;
    background-color: #d0d5dd;
    border-radius: 2px;
    margin: 12px auto 0;
  }

  .content-sidebar.mobile-hidden {
    transform: translateY(100%);
  }

  .content-sidebar.mobile-slide-in {
    transform: translateY(0);
  }

  .sidebar-content {
    height: auto;
    max-height: calc(85vh - 20px);
    overflow-y: auto;
    padding: 0 0 20px 0;
  }

  .content-info-heading {
    padding: 20px 20px 15px;
    border-bottom: 1px solid #d0d5dd;
    margin-bottom: 15px;
    color: var(--primary-blue);
    font-size: var(--fs-title-large, 22px);
    font-weight: var(--font-weight-medium);
  }

  .content-item {
    margin-bottom: 5px;
  }

  .content-title {
    padding: 10px 20px 8px;
    color: var(--black-body-text);
    font-size: var(--fs-title-big);
    font-weight: var(--font-weight-bold);
  }

  .content-value {
    padding: 0 20px 15px;
    gap: 10px;
  }

  .value-tag {
    font-size: 0.85rem;
    padding: 8px 14px;
    border-radius: 6px;
    font-weight: 500;
  }

  .edit-actions {
    padding: 15px 20px;
    margin-bottom: 10px;
  }

  /* Hide desktop elements on mobile */
  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: flex;
  }
}

@media (max-width: 480px) {
  .content-info-heading {
    padding: 15px 15px 10px;
    font-size: var(--fs-large);
  }

  .content-title {
    font-size: var(--fs-big);
    padding: 8px 15px 6px;
  }

  .content-value {
    padding: 0 15px 12px;
    gap: 8px;
  }

  .value-tag {
    font-size: var(--fs-small);
    padding: 6px 12px;
  }
}
</style>
